import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const LOGO_URL = "/cgi-logo.png";
const CGI_WEBSITE = "https://www.commongroundinstitute.net";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    if (!token) setError("This reset link is missing a token. Please request a new one.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated. Please sign in.");
      setTimeout(() => nav("/login"), 1500);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-10 bg-clinical" data-testid="reset-password-page">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="flex flex-col items-start gap-3 mb-10">
          <a href={CGI_WEBSITE} target="_blank" rel="noopener noreferrer" aria-label="Visit the Common Ground Institute website">
            <img src={LOGO_URL} alt="Common Ground Institute" className="h-20 w-auto object-contain rounded-md" />
          </a>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 mt-4">Choose a new password</h1>
          <p className="text-slate-600 leading-relaxed">
            Enter and confirm your new password below.
          </p>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6 sm:p-8">
            {done ? (
              <div className="text-center" data-testid="reset-success">
                <div className="h-12 w-12 rounded-full bg-green-50 text-green-700 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <h2 className="font-heading text-xl font-semibold text-slate-900">Password updated</h2>
                <p className="mt-2 text-slate-600 text-sm">Redirecting you to sign in…</p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-form">
                <div className="space-y-2">
                  <Label htmlFor="password">New password</Label>
                  <PasswordInput
                    id="password" autoComplete="new-password" required value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password-input"
                    testIdPrefix="reset-password"
                  />
                  <p className="text-xs text-slate-500">At least 6 characters.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm">Confirm new password</Label>
                  <PasswordInput
                    id="confirm" autoComplete="new-password" required value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-confirm-input"
                    testIdPrefix="reset-confirm"
                  />
                </div>
                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="reset-error">
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={submitting || !token}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                  data-testid="reset-submit-button"
                >
                  {submitting ? "Updating…" : "Update password"}
                </Button>
                <p className="text-sm text-slate-600 text-center">
                  <Link to="/login" className="text-primary font-medium hover:underline" data-testid="reset-to-login-link">
                    Back to sign in
                  </Link>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
